import styled from "styled-components";
import { theme } from "styles/theme";

export const TimeNavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0.75rem 0;
  margin-bottom: 1rem;
  box-shadow: none;
  gap: 1rem;
  min-height: auto;
  flex-wrap: wrap;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0.5rem 0;
    gap: 0.6rem;
  }
`;

export const NavigationButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid
    ${(props) =>
      props.disabled ? theme.colors.border : theme.colors.tertiary};
  border-radius: ${theme.borderRadius.small};
  background: transparent;
  color: ${(props) =>
    props.disabled ? theme.colors.textMuted : theme.colors.tertiary};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  font-size: 1.25rem;
  font-weight: bold;

  &:hover:not(:disabled) {
    background: ${theme.colors.tertiary};
    color: ${theme.colors.onTertiary};
  }

  &:disabled {
    opacity: 0.55;
  }
`;

export const TimeNavigationSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  flex: 1;
  min-width: 0;
  max-width: 100%;
`;

export const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  flex-wrap: wrap;
`;

export const TimeControlsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
`;

export const NowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  height: 44px;
  padding: 0 1rem;
  border: 2px solid ${theme.colors.tertiary};
  border-radius: ${theme.borderRadius.small};
  background: ${theme.colors.tertiary};
  color: ${theme.colors.onTertiary};
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease,
    opacity 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${theme.colors.tertiaryDark};
    border-color: ${theme.colors.tertiaryDark};
  }

  &:disabled {
    background: transparent;
    border-color: ${theme.colors.border};
    color: ${theme.colors.textMuted};
    cursor: not-allowed;
    opacity: 0.55;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    min-width: 56px;
    padding: 0 0.75rem;
    font-size: 0.8rem;
  }
`;
export const PeriodLabel = styled.div`
  color: ${theme.colors.tertiary};
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  min-height: 1.2rem;
  white-space: nowrap;
`;

export const TimeRangeButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${theme.colors.bgLight};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.small};
  padding: 0.5rem;
  width: auto;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;

  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: 0.25rem;
    padding: 0.3rem;
  }
`;

export const TimeButton = styled.button<{ $active: boolean }>`
  padding: 0.65rem 1rem;
  border: 2px solid
    ${(props) =>
      props.$active ? theme.colors.tertiary : "transparent"};
  border-radius: ${theme.borderRadius.small};
  background: ${(props) =>
    props.$active ? theme.colors.tertiary : "transparent"};
  color: ${(props) =>
    props.$active ? theme.colors.onTertiary : theme.colors.textPrimary};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  min-width: 44px;

  &:hover {
    background: ${(props) =>
      props.$active
        ? theme.colors.tertiaryDark
        : theme.colors.tertiaryTransparent};
    border-color: ${theme.colors.tertiary};
    color: ${(props) =>
      props.$active ? theme.colors.onTertiary : theme.colors.textPrimary};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
`;

export const ViewControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${theme.colors.bgLight};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.small};
  padding: 0.5rem;
  flex-shrink: 0;
`;

export const ViewButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem 0.8rem;
  min-height: 36px;
  border: 1px solid
    ${(props) => (props.$active ? theme.colors.tertiary : "transparent")};
  border-radius: ${theme.borderRadius.small};
  background: ${(props) =>
    props.$active ? theme.colors.tertiary : "transparent"};
  color: ${(props) =>
    props.$active ? theme.colors.onTertiary : theme.colors.tertiary};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &:hover {
    background: ${(props) =>
      props.$active ? theme.colors.tertiaryDark : theme.colors.tertiaryTransparent};
    border-color: ${theme.colors.tertiary};
  }
`;

export const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const ListChartCard = styled.div`
  background-color: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
  width: 100%;
`;

export const ListChartContainer = styled.div`
  padding: 0;
  height: 400px;
  position: relative;
  overflow: visible;
`;

export const CombinedChartWrapper = styled.div`
  background-color: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
  height: 500px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 360px;
  }
`;

export const CombinedContainer = styled.div`
  padding: 0;
  height: 100%;
  position: relative;
  overflow: visible;
`;
